import React from 'react';
import cx from 'classnames';

import { getUniqueKey } from 'app/utils/helpers';
import TokenImage from 'app/components/ui/TokenImage';
import DynamicsWithTooltip from 'app/components/ui/DynamicsWithTooltip';
import { ChangeTypes } from 'app/types/info-tabs';
import { ReactComponent as Tezos } from 'app/components/svg/Tezos.svg';
import { CurrencyEnum } from 'app/components/ui/CurrencySwitcher/CurrencySwitcher';

import s from './InfoTab.module.sass';

export type PooledAssets = {
  id: number
  logo: string
  value: string
  name: string
};

type InfoTabProps = {
  title: string
  value?: number
  change?: ChangeTypes
  isPooled?: boolean
  pooledAssets?: PooledAssets[]
  token?: string
  className?: string
  sizeT?: keyof typeof sizeClass
  currency?: CurrencyEnum
  loading?: boolean
  xtzOnly?: boolean
};

type LayoutProps = {
  title: string
  isPooled?: boolean
  className?: string
  sizeT: keyof typeof sizeClass
};

const sizeClass = {
  medium: s.medium,
  fluid: s.fluid,
};

const Layout: React.FC<LayoutProps> = ({
  children,
  title,
  isPooled,
  className,
  sizeT,
}) => {
  const compoundClassName = cx(
    s.root,
    { [s.pooled]: isPooled },
    sizeClass[sizeT],
    className,
  );

  return (
    <div className={compoundClassName}>
      <div className={cx(s.title)}>{title}</div>
      {children}
      <div className={cx(s.footer)} />
    </div>
  );
};

const InfoTab: React.FC<InfoTabProps> = ({
  title,
  value,
  change,
  pooledAssets,
  className,
  sizeT = 'medium',
  currency = CurrencyEnum.XTZ,
  loading,
  xtzOnly,
}) => {
  if (pooledAssets && pooledAssets.length) {
    return (
      <Layout sizeT={sizeT} title={title} className={className} isPooled>
        <div className={cx(s.info, s.pooled)}>
          {pooledAssets.map((item) => (
            <div key={getUniqueKey()} className={cx(s.asset)}>
              <TokenImage
                tokensNames={{
                  firstTokenName: item.name,
                }}
                firstTokenImage={item.logo}
                sizeT="medium"
                className={cx(s.pooledLogo)}
              />

              <div className={cx(s.currency)}>
                {loading ? '...' : item.value}
              </div>

              <span className={cx(s.token)}>{item.name}</span>
            </div>
          ))}
        </div>
      </Layout>
    );
  }

  const tezosPrice = title === 'Tezos USD price:';

  return (
    <Layout sizeT={sizeT} title={title} className={className}>
      <div className={cx(s.info)}>
        <div className={cx(s.currency)}>
          {change
          && (
          <>
            <span className={cx(
              s.currentCurrency,
              { [s.usd]: currency === CurrencyEnum.USD || tezosPrice },
            )}
            >
              $
            </span>
            <Tezos className={cx(
              s.currentCurrency,
              { [s.xtz]: currency === CurrencyEnum.XTZ && !tezosPrice },
            )}
            />
          </>
          )}
          &nbsp;
          {loading ? '...' : value}
        </div>

        {change && <DynamicsWithTooltip change={change} xtzOnly={xtzOnly} />}
      </div>
    </Layout>
  );
};

export default InfoTab;
